import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Grid,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DashboardHeader from '../components/DashboardHeader';
import { WorkersTable } from '../components/WorkersTable';
import { TimeCalendar } from '../components/TimeCalendar';
import { WorkerScheduleManager } from '../components/WorkerScheduleManager';
import { Worker, CreateWorkerData, CreateWorkerScheduleData, WorkerSchedule } from '../types';
import { workerService } from '../services/workerService';
import { companyService } from '../services/companyService';
import { workerScheduleService } from '../services/workerScheduleService';
import { useAuth } from '../contexts/AuthContext';

export const WorkersPage: React.FC = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const { user } = useAuth();
  const [credForm, setCredForm] = useState({ new_company_id: '', new_pin: '' });
  const [savingCreds, setSavingCreds] = useState(false);
  const [credMsg, setCredMsg] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Cargar empresas al montar el componente
  useEffect(() => {
    console.log('Current user:', user);
    loadCompanies();
    // Si es empresa, refrescar su imagen para el header
    const refreshLogo = async () => {
      try {
        if (user?.role === 'empresa') {
          const me = await companyService.getMyCompany();
          if (me?.image_url) {
            // actualizar user local para que el header muestre el logo
            (user as any).image_url = me.image_url;
          }
        }
      } catch (e) {
        // continuar sin bloquear
      }
    };
    refreshLogo();
  }, [user]);

  // Seleccionar la primera empresa cuando se cargan las empresas (solo para crear trabajador)
  useEffect(() => {
    if (companies.length > 0 && formData.company_id === '' && showCreateDialog) {
      setFormData(prev => ({ ...prev, company_id: companies[0].id.toString() }));
    }
  }, [companies, showCreateDialog]);

  const loadCompanies = async () => {
    try {
      console.log('Loading companies...');
      // Si el usuario es super_admin, cargar todas las empresas
      if (user?.role === 'super_admin') {
        const data = await companyService.getAllCompanies();
        console.log('Companies loaded:', data);
        setCompanies(data);
      } else {
        // Si es empresa, no cargar empresas (no necesita seleccionar)
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setCompanies([]);
    }
  };

  // Form states
  const [formData, setFormData] = useState({
    worker_id: '',
    email: '',
    price_per_hour: '',
    role: 'Trabajador',
    pin: '',
    name: '',
    overtime_price: '',
    company_id: '',
  });

  // Estado para los horarios
  const [schedules, setSchedules] = useState<{ [key: string]: { entry_time: string; exit_time: string } }>({
    Monday: { entry_time: '', exit_time: '' },
    Tuesday: { entry_time: '', exit_time: '' },
    Wednesday: { entry_time: '', exit_time: '' },
    Thursday: { entry_time: '', exit_time: '' },
    Friday: { entry_time: '', exit_time: '' },
    Saturday: { entry_time: '', exit_time: '' },
    Sunday: { entry_time: '', exit_time: '' },
  });

  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Función para refrescar la tabla de trabajadores
  const refreshWorkersTable = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCreateWorker = () => {
    setFormData({
      worker_id: '',
      email: '',
      price_per_hour: '',
      role: 'Trabajador',
      pin: '',
      name: '',
      overtime_price: '',
      company_id: user?.company_id?.toString() || '1', // Asignar la empresa del usuario logueado
    });
    
    // Resetear horarios para crear trabajador
    setSchedules({
      Monday: { entry_time: '', exit_time: '' },
      Tuesday: { entry_time: '', exit_time: '' },
      Wednesday: { entry_time: '', exit_time: '' },
      Thursday: { entry_time: '', exit_time: '' },
      Friday: { entry_time: '', exit_time: '' },
      Saturday: { entry_time: '', exit_time: '' },
      Sunday: { entry_time: '', exit_time: '' },
    });
    
    setScheduleError(null);
    setShowCreateDialog(true);
  };

  const handleEditWorker = async (worker: Worker) => {
    // Configurar el formulario PRIMERO (igual que crear)
    setFormData({
      worker_id: worker.worker_id || '',
      email: worker.email || '',
      price_per_hour: worker.price_per_hour.toString(),
      role: worker.role,
      pin: worker.pin || '',
      name: worker.name,
      overtime_price: worker.overtime_price.toString(),
      company_id: worker.company_id.toString(),
    });
    
    // Resetear horarios (igual que crear)
    setSchedules({
      Monday: { entry_time: '', exit_time: '' },
      Tuesday: { entry_time: '', exit_time: '' },
      Wednesday: { entry_time: '', exit_time: '' },
      Thursday: { entry_time: '', exit_time: '' },
      Friday: { entry_time: '', exit_time: '' },
      Saturday: { entry_time: '', exit_time: '' },
      Sunday: { entry_time: '', exit_time: '' },
    });
    
    setSelectedWorker(worker);
    setShowEditDialog(true);
    
    // DESPUÉS de abrir el diálogo, cargar los horarios
    try {
      const existingSchedules = await workerScheduleService.getWorkerSchedules(worker.id);
      
      // Llenar con los horarios existentes
      const updatedSchedules = {
        Monday: { entry_time: '', exit_time: '' },
        Tuesday: { entry_time: '', exit_time: '' },
        Wednesday: { entry_time: '', exit_time: '' },
        Thursday: { entry_time: '', exit_time: '' },
        Friday: { entry_time: '', exit_time: '' },
        Saturday: { entry_time: '', exit_time: '' },
        Sunday: { entry_time: '', exit_time: '' },
      };

      existingSchedules.forEach((schedule: any) => {
        const day = schedule.day_of_week;
        
        if (updatedSchedules[day as keyof typeof updatedSchedules]) {
          const entryTime = schedule.entry_time ? schedule.entry_time.substring(0, 5) : '';
          const exitTime = schedule.exit_time ? schedule.exit_time.substring(0, 5) : '';
          
          updatedSchedules[day as keyof typeof updatedSchedules] = {
            entry_time: entryTime,
            exit_time: exitTime
          };
        }
      });

      setSchedules(updatedSchedules);
      
      // Pequeño delay para asegurar que el estado se actualice
      setTimeout(() => {
        setSchedules(updatedSchedules);
      }, 100);
    } catch (error) {
      console.error('Error cargando horarios existentes:', error);
    }
  };

  const handleViewCalendar = (worker: Worker) => {
    setSelectedWorker(worker);
    setShowCalendar(true);
  };

  const handleViewScheduleManager = (worker: Worker) => {
    setSelectedWorker(worker);
    setShowScheduleManager(true);
  };

  const handleScheduleChange = (day: string, field: 'entry_time' | 'exit_time', value: string) => {
    setSchedules(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
    setScheduleError(null);
  };

  const validateSchedules = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    for (const day of days) {
      const schedule = schedules[day];
      if (schedule.entry_time && schedule.exit_time) {
        // Permitir horarios nocturnos (como 7 PM a 7 AM)
        // Solo validar si ambas horas están en el mismo día
        const entryHour = parseInt(schedule.entry_time.split(':')[0]);
        const exitHour = parseInt(schedule.exit_time.split(':')[0]);
        
        // Si la hora de salida es menor que la de entrada, asumimos que es un turno nocturno
        // y está bien (ej: 19:00 a 07:00)
        if (schedule.entry_time === schedule.exit_time) {
          setScheduleError(`La hora de entrada no puede ser igual a la hora de salida para ${day}`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      console.log('Iniciando guardado de trabajador...');
      console.log('Schedules configurados:', schedules);
      
      // Validar campos requeridos
      if (!formData.name.trim()) {
        alert('El campo "Nombres" es obligatorio');
        return;
      }
      if (!formData.price_per_hour) {
        alert('El campo "Precio Hora" es obligatorio');
        return;
      }
      if (!formData.overtime_price) {
        alert('El campo "Precio overtime" es obligatorio');
        return;
      }
      if (!formData.company_id) {
        alert('Debe seleccionar una empresa');
        return;
      }
      
      // Para empresas, usar automáticamente su company_id
      const finalCompanyId = user?.role === 'super_admin' 
        ? parseInt(formData.company_id) 
        : user?.id; // Usar user.id que es el ID de la empresa

      if (!finalCompanyId) {
        alert('Error: No se pudo determinar la empresa');
        return;
      }
      
      // Validar horarios si se están configurando
      const hasSchedules = Object.values(schedules).some(s => s.entry_time || s.exit_time);
      console.log('¿Tiene horarios configurados?', hasSchedules);
      
      if (hasSchedules && !validateSchedules()) {
        console.log('Error de validación en horarios');
        return;
      }

      // Preparar horarios para enviar
      const schedulesToSend = hasSchedules ? Object.entries(schedules)
        .filter(([day, schedule]) => schedule.entry_time && schedule.exit_time)
        .map(([day, schedule]) => ({
          day_of_week: day,
          entry_time: schedule.entry_time + ':00',
          exit_time: schedule.exit_time + ':00',
          is_working_day: true
        })) : undefined;

      let newWorkerId: number;

      if (showEditDialog && selectedWorker) {
        // Para editar, incluir horarios en la actualización
        const updateData: any = {
          name: formData.name,
          email: formData.email,
          pin: formData.pin,
          company_id: finalCompanyId,
          price_per_hour: parseFloat(formData.price_per_hour),
          overtime_price: parseFloat(formData.overtime_price),
          role: formData.role,
          schedules: schedulesToSend
        };
        await workerService.updateWorker(selectedWorker.id, updateData);
        newWorkerId = selectedWorker.id;
      } else {
        // Para crear, incluir horarios en la creación
        const createData: any = {
          worker_id: formData.worker_id || undefined,
          name: formData.name,
          email: formData.email || undefined,
          pin: formData.pin || undefined,
          company_id: finalCompanyId,
          price_per_hour: parseFloat(formData.price_per_hour),
          overtime_price: parseFloat(formData.overtime_price),
          role: formData.role,
          schedules: schedulesToSend
        };
        console.log('Datos del trabajador a crear:', createData);
        console.log('finalCompanyId:', finalCompanyId);
        console.log('user:', user);
        
        // Validar que tenemos company_id
        if (!createData.company_id) {
          alert('Error: No se puede determinar la empresa para el trabajador');
          return;
        }
        
        const newWorker = await workerService.createWorker(createData);
        console.log('Trabajador creado:', newWorker);
        newWorkerId = newWorker.id;
      }

      console.log('ID del trabajador:', newWorkerId);
      console.log('Trabajador guardado exitosamente con horarios');

      setShowCreateDialog(false);
      setShowEditDialog(false);
      setSelectedWorker(null);
      
      // Refrescar la tabla de trabajadores
      refreshWorkersTable();
         } catch (error: any) {
       console.error('Error saving worker:', error);
       alert(`Error al guardar: ${error?.message || 'Error desconocido'}`);
     }
  };

  const DAYS_OF_WEEK = [
    { value: 'Monday', label: 'Lunes' },
    { value: 'Tuesday', label: 'Martes' },
    { value: 'Wednesday', label: 'Miércoles' },
    { value: 'Thursday', label: 'Jueves' },
    { value: 'Friday', label: 'Viernes' },
    { value: 'Saturday', label: 'Sábado' },
    { value: 'Sunday', label: 'Domingo' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#f7f9fc', p: { xs: 2, md: 4 } }}>
      <DashboardHeader
        title="Gestión de Trabajadores"
        subtitleChips={user?.role === 'empresa' ? [user?.name || 'Empresa'] : ["Admin/Super Admin"]}
        showNav
        currentPage="workers"
      />
      
      {user?.role === 'empresa' && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Mi Empresa</Typography>
            <Typography sx={{ mb: 2, color: 'text.secondary' }}>Nombre: {user?.name} · ID actual: {(user as any)?.company_id}</Typography>
            {credMsg && (
              <Alert severity="success" sx={{ mb: 2 }}>{credMsg}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nuevo ID de empresa"
                  fullWidth
                  value={credForm.new_company_id}
                  onChange={(e) => setCredForm({ ...credForm, new_company_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nuevo PIN"
                  type="password"
                  fullWidth
                  value={credForm.new_pin}
                  onChange={(e) => setCredForm({ ...credForm, new_pin: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  disabled={savingCreds}
                  onClick={async () => {
                    try {
                      setSavingCreds(true);
                      setCredMsg(null);
                      // Si el usuario es admin o super_admin, necesitamos enviar el ID de la empresa
                      const data: { new_company_id?: string; new_pin?: string; company_id?: number } = {
                        new_company_id: credForm.new_company_id || undefined,
                        new_pin: credForm.new_pin || undefined,
                      };
                      
                      // Añadir el ID de la empresa si el usuario es admin o super_admin
                      if (user?.role === 'admin' || user?.role === 'super_admin') {
                        // Usar el ID de la empresa seleccionada o la empresa actual
                        data.company_id = selectedWorker?.company_id || user?.company_id;
                      }
                      
                      await companyService.updateMyCredentials(data);
                      setCredMsg('Datos de empresa actualizados');
                      setCredForm({ new_company_id: '', new_pin: '' });
                    } catch (e: any) {
                      alert(e?.response?.data?.message || 'No se pudieron actualizar los datos');
                    } finally {
                      setSavingCreds(false);
                    }
                  }}
                >
                  {savingCreds ? 'Guardando...' : 'Guardar cambios'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      <WorkersTable
        onEditWorker={handleEditWorker}
        onViewCalendar={handleViewCalendar}
        onViewScheduleManager={handleViewScheduleManager}
        onCreateWorker={handleCreateWorker}
        refreshTrigger={refreshTrigger}
      />

             {/* Create/Edit Worker Dialog */}
       <Dialog open={showCreateDialog || showEditDialog} onClose={() => {
         setShowCreateDialog(false);
         setShowEditDialog(false);
         setSelectedWorker(null);
       }} maxWidth="lg" fullWidth>
        <DialogTitle>
          {showEditDialog ? 'Editar Trabajador' : 'Crear Nuevo Trabajador'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Información básica del trabajador */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Columna Izquierda */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                <TextField
                  label="ID de Trabajador"
                  placeholder="ID (opcional)"
                  value={formData.worker_id}
                  onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
                  fullWidth
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />

                <TextField
                  label="Email *"
                  placeholder="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  fullWidth
                  required
                />

                <TextField
                  label="Precio Hora *"
                  placeholder="Precio"
                  type="number"
                  value={formData.price_per_hour}
                  onChange={(e) => setFormData({ ...formData, price_per_hour: e.target.value })}
                  fullWidth
                  required
                />

                <FormControl fullWidth required>
                  <InputLabel>Tipo de trabajador *</InputLabel>
                  <Select
                    value={formData.role}
                    label="Tipo de trabajador *"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <MenuItem value="Trabajador">Trabajador</MenuItem>
                    <MenuItem value="Jefe de empresa">Jefe de empresa</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Columna Derecha */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                <TextField
                  label="Pin *"
                  placeholder="Pin"
                  type="password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />

                <TextField
                  label="Nombres *"
                  placeholder="Nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />

                <TextField
                  label="Precio overtime *"
                  placeholder="Precio"
                  type="number"
                  value={formData.overtime_price}
                  onChange={(e) => setFormData({ ...formData, overtime_price: e.target.value })}
                  fullWidth
                  required
                />

                {/* Solo mostrar selector de empresa si es super_admin */}
                {user?.role === 'super_admin' && (
                  <FormControl fullWidth>
                    <InputLabel>Empresa asociada</InputLabel>
                    <Select
                      value={formData.company_id}
                      label="Empresa asociada"
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    >
                      {companies.length === 0 ? (
                        <MenuItem disabled>No hay empresas disponibles</MenuItem>
                      ) : (
                        companies.map((company) => (
                          <MenuItem key={company.id} value={company.id.toString()}>
                            {company.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>

            {/* Divider */}
            <Divider />

                         {/* Sección de Horarios */}
             <Box>
               <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                 Configurar Horarios de Trabajo
               </Typography>
               <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                 Establece los horarios de entrada y salida para cada día de la semana (opcional)
               </Typography>

               {scheduleError && (
                 <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
                   {scheduleError}
                 </Alert>
               )}

               <Paper sx={{ p: 2 }}>
                 <TableContainer>
                   <Table size="small">
                     <TableHead>
                       <TableRow>
                         <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>Día</TableCell>
                         <TableCell sx={{ fontWeight: 'bold' }}>Hora de Entrada</TableCell>
                         <TableCell sx={{ fontWeight: 'bold' }}>Hora de Salida</TableCell>
                       </TableRow>
                     </TableHead>
                     <TableBody>
                       {DAYS_OF_WEEK.map((day) => (
                         <TableRow key={day.value} hover>
                           <TableCell>
                             <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                               {day.label}
                             </Typography>
                           </TableCell>
                           <TableCell>
                             <TextField
                               type="time"
                               value={schedules[day.value]?.entry_time || ''}
                               onChange={(e) => handleScheduleChange(day.value, 'entry_time', e.target.value)}
                               InputLabelProps={{ shrink: true }}
                               size="small"
                               fullWidth
                               sx={{ maxWidth: '150px' }}
                             />
                           </TableCell>
                           <TableCell>
                             <TextField
                               type="time"
                               value={schedules[day.value]?.exit_time || ''}
                               onChange={(e) => handleScheduleChange(day.value, 'exit_time', e.target.value)}
                               InputLabelProps={{ shrink: true }}
                               size="small"
                               fullWidth
                               sx={{ maxWidth: '150px' }}
                             />
                           </TableCell>
                         </TableRow>
                       ))}
                     </TableBody>
                   </Table>
                 </TableContainer>
               </Paper>
             </Box>
          </Box>
        </DialogContent>
                 <DialogActions>
           <Button onClick={() => {
             setShowCreateDialog(false);
             setShowEditDialog(false);
             setSelectedWorker(null);
           }}>Cancelar</Button>
           <Button onClick={handleSubmit} variant="contained">
             Guardar
           </Button>
         </DialogActions>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog 
        open={showCalendar} 
        onClose={() => setShowCalendar(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Calendario de Tiempo</Typography>
            <Button onClick={() => setShowCalendar(false)}>Cerrar</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWorker && (
            <TimeCalendar 
              worker={selectedWorker} 
              onClose={() => setShowCalendar(false)}
              userRole={user?.role}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Manager Dialog */}
      <Dialog 
        open={showScheduleManager} 
        onClose={() => setShowScheduleManager(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Gestor de Horarios</Typography>
            <Button onClick={() => setShowScheduleManager(false)}>Cerrar</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWorker && (
            <WorkerScheduleManager 
              workerId={selectedWorker.id}
              workerName={selectedWorker.name}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
